import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FC, useEffect, useRef, useState, type JSX } from 'react';
import { useAppSelector } from '@/store/hooks/redux';
import EmptyImageCard from '../EmptyImageCard';
import { IExtendedPreviewPropsModel } from '@/editor/shared/components/forms/creatives/models/basic-preview-props.model';
import { bidtheatreClientStyles } from './BidtheatreClientStyle';
import { useRegularTemplateData } from '@/shared/hooks/useTemplateData';
import CreativeTypesEnum from '@/editor/shared/constants/creatives-types.enum';
import { useTheme, useMediaQuery } from '@mui/material';

const HettingerBidtheatrePreview: FC<IExtendedPreviewPropsModel> = ({
  description,
  propertyPriceQuote,
  employeeName,
  employeeAvatar,
  employeePhone,
  employeeEmail,
  companyExternalId,
  profileTitle,
  selectedType,
  caption,
}): JSX.Element => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const bidtheatreClientStyle = companyExternalId ? bidtheatreClientStyles[companyExternalId] || {} : {};

  const singleMedia = useAppSelector(
    (state) => state.creativeStepSliceReducer?.creativeFormsState?.bidtheatre?.single?.media
  );

  const dynamicMedia = useAppSelector(
    (state) => state.creativeStepSliceReducer?.creativeFormsState?.bidtheatre?.dynamic?.creativeMedias
  );
  const bidtheatreShareLink = useAppSelector((state) => state.propertyInfoSliceReducer.bidtheatreShareLink);

  const isRegularTemplateData = useRegularTemplateData();

  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);

  const [index, setIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const totalSlides = dynamicMedia.length;
  useEffect(() => {
    if (selectedType === CreativeTypesEnum.DYNAMIC) {
      startAutoSlide();
      return stopAutoSlide;
    }
  }, [selectedType]);

  const startAutoSlide = () => {
    intervalRef.current = setInterval(() => {
      handleNext();
    }, 4000);
  };

  const stopAutoSlide = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  };
  const handleNext = () => {
    if (index === totalSlides) return;
    setIsTransitioning(true);
    setIndex((prev) => prev + 1);
  };

  useEffect(() => {
    if (index === totalSlides) {
      const timeout = setTimeout(() => {
        setIsTransitioning(false);
        setIndex(0);
      }, 600);
      return () => clearTimeout(timeout);
    }
  }, [index, totalSlides]);

  useEffect(() => {
    if (employeeAvatar instanceof File) {
      const url = URL.createObjectURL(employeeAvatar);
      setAvatarUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setAvatarUrl(employeeAvatar);
    }
  }, [employeeAvatar]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: '100vw',
        height: isMobile ? 'auto' : '450px',
        minHeight: isMobile ? '420px' : '450px',
        fontFamily: 'Apercu Pro',
        border: '1px solid #ccc',
        overflow: 'hidden',
        display: isMobile ? 'flex' : 'block',
        flexDirection: isMobile ? 'column' : undefined,
        alignItems: isMobile ? 'center' : undefined,
      }}>
      {selectedType === CreativeTypesEnum.SINGLE ||
      selectedType === CreativeTypesEnum.CAROUSEL ||
      selectedType == CreativeTypesEnum.VIDEO ? (
        singleMedia?.preSignedUrl ? (
          <Box
            component="a"
            target="_blank"
            href={bidtheatreShareLink}
            sx={{
              position: isMobile ? 'relative' : 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: isMobile ? '180px' : '300px',
              backgroundImage: `url(${singleMedia?.preSignedUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        ) : (
          <EmptyImageCard />
        )
      ) : dynamicMedia?.[0]?.preSignedUrl ? (
        <Box sx={{ width: '100%', height: isMobile ? '180px' : '300px', overflow: 'hidden', position: 'relative' }}>
          <a
            target="_blank"
            href={bidtheatreShareLink}
            style={{
              display: 'flex',
              width: `${750 * (totalSlides + 1)}px`,
              height: '100%',
              transform: `translateX(-${index * 750}px)`,
              transition: isTransitioning ? 'transform 0.6s ease-in-out' : 'none',
              textDecoration: 'none',
            }}
            onTransitionEnd={() => setIsTransitioning(true)}>
            {dynamicMedia.map((src, i) => (
              <img
                key={i}
                src={src.preSignedUrl}
                alt={`slide-${i}`}
                style={{
                  width: `750px`,
                  height: '100%',
                  objectFit: 'cover',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  flexShrink: 0,
                }}
              />
            ))}
            <img
              src={dynamicMedia[0].preSignedUrl}
              alt="cloned-slide"
              style={{
                width: `750px`,
                height: '100%',
                objectFit: 'cover',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                flexShrink: 0,
              }}
            />
          </a>
        </Box>
      ) : (
        <EmptyImageCard />
      )}
      <Box
        sx={{
          position: isMobile ? 'relative' : 'absolute',
          top: isMobile ? undefined : '300px',
          left: isMobile ? undefined : 0,
          width: isMobile ? '100%' : '65%',
          height: isMobile ? 'auto' : '150px',
          backgroundColor: 'rgba(0,0,0,0.07)',
          padding: isMobile ? '8px' : '12px',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'center' : undefined,
        }}>
        <Typography
          sx={{
            fontFamily: 'Cormorant Semi',
            fontSize: isMobile ? '16px' : '21px',
            lineHeight: isMobile ? '20px' : '26px',
            color: '#000',
            textAlign: isMobile ? 'center' : 'left',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
          {' '}
          {description}
        </Typography>
        <Box>
          <Typography
            sx={{
              fontSize: isMobile ? '11px' : '13px',
              textAlign: isMobile ? 'center' : 'left',
              fontFamily: 'Apercu Pro',
            }}>
            {caption}
          </Typography>
          <Typography
            sx={{
              fontSize: isMobile ? '11px' : '13px',
              textAlign: isMobile ? 'center' : 'left',
              fontFamily: 'Apercu Pro',
            }}>
            {propertyPriceQuote}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: isMobile ? 'flex' : 'block',
          flexDirection: isMobile ? 'column' : undefined,
          position: isMobile ? 'relative' : 'absolute',
          right: isMobile ? undefined : '55px',
          bottom: isMobile ? undefined : '100px',
          top: isMobile ? undefined : undefined,
          width: isMobile ? 'auto' : '80px',
          height: isMobile ? 'auto' : '85px',
          zIndex: 2,
          mt: isMobile ? 2 : 0,
        }}>
        <img
          src={avatarUrl || undefined}
          alt="Portrait"
          style={{
            width: isMobile ? '60px' : '80px',
            height: isMobile ? '65px' : '85px',
            objectFit: 'cover',
          }}
        />
        <Box
          sx={{
            mt: isMobile ? 1 : 0,
            width: isMobile ? '100%' : '80px',
            textAlign: 'left',
            position: isMobile ? 'relative' : 'absolute',
            top: isMobile ? undefined : '95px',
            right: 0,
          }}>
          <Typography sx={{ fontSize: isMobile ? '9px' : '11px', fontWeight: 600, fontFamily: 'Apercu Pro' }}>
            {employeeName}
          </Typography>
          <Typography sx={{ fontSize: isMobile ? '8px' : '7px', fontFamily: 'Apercu Pro' }}>
            {profileTitle}
            <br />
            {employeeEmail}
            <br />
            {employeePhone}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          position: isMobile ? 'relative' : 'absolute',
          top: isMobile ? undefined : '415px',
          right: isMobile ? undefined : '60px',
          width: isMobile ? '80px' : '80px',
          height: isMobile ? '14px' : '19px',
          mt: isMobile ? 2 : 0,
          mb: isMobile ? 1 : 0,
          mx: isMobile ? 'auto' : 0,
          display: isMobile ? 'block' : undefined,
        }}>
        <img
          src={bidtheatreClientStyle.logo}
          alt="Hettinger logo"
          style={{
            width: isMobile ? '80px' : '120px',
            objectFit: 'contain',
            display: 'block',
            margin: isMobile ? '0 auto' : undefined,
          }}
        />
      </Box>
      <Box
        sx={{
          position: isMobile ? 'absolute' : 'absolute',
          top: isMobile ? '10px' : '22px',
          left: isMobile ? '6px' : '12px',
          backgroundColor: 'rgb(237 237 237 / 80%)',
          fontSize: isMobile ? '7px' : '9px',
          fontFamily: 'Apercu Pro',
          width: isMobile ? '45px' : '55px',
          height: isMobile ? '13px' : '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        till bostaden
      </Box>
    </Box>
  );
};

export default HettingerBidtheatrePreview;
